import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// #dependency-cycle-detected
import { FirestorePathsService } from './firestorePaths.service';
import { DateService } from './date.service';
import { FirestoreService } from './firestore.service';
import { SessionService } from './session.service';
import type { TTrip, TTicket, TRoute, TTimestamp, TQueryOptions } from '@shared/models';
import { Constants } from '@shared/models';
const DateTimeFormats = Constants.DateTimeFormats;

@Injectable({
    'providedIn': 'root'
})
export class TripsService {
    constructor(private firestoreService: FirestoreService, private sessionService: SessionService,
        private firestorePathsService: FirestorePathsService, private dateService: DateService) {
    }

    public getNullable(): TTrip {
        const trip = {
            type: 'Actual',
            uid: ''
        } as TTrip;
        return trip;
    }

    public getObservable(): Observable<TTrip[]> {
        const path = this.firestorePathsService.getTripsPath();
        const observable = this.firestoreService.getObservable(path);
        return observable as Observable<TTrip[]>;
    }

    public async getByUid(uid: string): Promise<TTrip> {
        const path = this.firestorePathsService.getTripsPath();
        return this.firestoreService.getByUid(path, uid) as Promise<TTrip>;
    }

    public async getAll(): Promise<TTrip[]> {
        const path = this.firestorePathsService.getTripsPath();
        return this.firestoreService.getAll(path) as Promise<TTrip[]>;
    }

    public async add(trip: TTrip): Promise<TTrip> {
        const path = this.firestorePathsService.getTripsPath();
        return this.firestoreService.add(path, trip) as Promise<TTrip>;
    }

    public async set(trip: TTrip): Promise<TTrip> {
        const path = this.firestorePathsService.getTripsPath();
        return this.firestoreService.set(path, trip) as Promise<TTrip>;
    }

    public async update(trip: TTrip): Promise<TTrip> {
        const path = this.firestorePathsService.getTripsPath();
        return this.firestoreService.update(path, trip) as Promise<TTrip>;
    }

    public async delete(uid: string): Promise<void> {
        const path = this.firestorePathsService.getTripsPath();
        return this.firestoreService.delete(path, uid);
    }

    //
    public sort(trips: TTrip[]): TTrip[] {
        trips.sort((a, b) => {
            if (a.startTripDate < b.startTripDate) {
                return -1;
            } else if (a.startTripDate > b.startTripDate) {
                return 1;
            } else {
                return 0;
            }
        });
        return trips;
    }

    public async getTrips(tripUids: string[]): Promise<TTrip[]> {
        const trips: TTrip[] = [];

        for (const uid of tripUids) {
            const trip = await this.getByUid(uid);
            trips.push(trip);
        }
        return trips;
    }

    public async startTrip(tickets: TTicket[], route: TRoute, driverName = ''): Promise<TTrip> {
        const collection = this.firestorePathsService.getTripsPath();

        const trip = {
            'driver': driverName ? driverName : this.sessionService.getUsersFullName,
            'status': 'driving',
            'startTripDate': this.dateService.now(),
            'uid': route.shuttle.shuttleName + '_' + this.dateService.formatTimestamp('now', DateTimeFormats.SortDateTime)
        } as TTrip;


        // trip.status = 'driving';
        // trip.startTripDate = this.dateService.now();
        // trip.uid = this.firestoreService.createUid();
        // trip.uid = route.shuttle.shuttleName + "_" + this.dateService.formatTimestamp('now', DateTimeFormats.SortDateTime);

        // Suppose I want to test NavigationPage4 and see if the current ticket's
        // background color is set correctly.  In order to get to NavigationPage4 I need
        // to have previously visited NavigationPage3 and clicked the 'Start Route' button
        // (Starting the route will call this method and set the startTripDate.)
        // Assume that once on NavigationPage4 I check the color or a bug happens and I
        // go back and not complete the trip.  This leaves the trip in an unfinished state
        // and causes problems when generating trip reports.  In order to prevent problems I
        // will set a few values here that should by all rights be set in the endTrip() method.
        trip.endTripDate = trip.startTripDate;
        trip.tickets = tickets;         // Present and accounted for tickets

        delete route.selectedTickets;   // We don't need all the tickets that defined the initial route anymore.

        // route.forEach(r => delete r.selectedTickets);   // We don't need the tickets that defined the route anymore.
        trip.plannedRoute = route;

        await this.firestoreService.set(collection, trip);
        return trip;
    }

    //
    public async updateTicket(tripUid: string, ticket: TTicket): Promise<TTrip> {
        const trip = await this.getByUid(tripUid);
        const idx = trip.tickets.findIndex((t) => (t.uid === ticket.uid));
        trip.tickets[idx] = ticket;
        return this.update(trip);
        // return this.updateTicketStatus(tripUid, ticket, 'current');


        // const trip = await this.getByUid(tripUid);
        // const idx = trip.actualRoute.selectedTickets.findIndex(t => {
        //     return (t.uid === ticket.uid);
        // });
        // trip.actualRoute.selectedTickets[idx] = ticket;
        // return this.update(trip);
        // return this.updateTicketStatus(tripUid, ticket, 'current');
    }

    // public async updateTicket(tripUid: string, ticket: TTicket) {
    //     return this.updateTicketStatus(tripUid, ticket, 'completed');
    //     // const promise = new Promise(async (resolve, reject) => {
    //     //     try {
    //     //         const collection = await this.firestorePathsService.getTripTicketsPath(tripUid);
    //     //         const tick: TTicket = await this.firestoreService.getByUid(collection, ticket.uid);
    //     //         if (tick) {
    //     //             ticket.status = 'completed';
    //     //             ticket.completedDate = mom-ent().valueOf();
    //     //             await this.firestoreService.update(collection, ticket);
    //     //         }
    //     //         resolve(true);
    //     //     } catch (err) {
    //     //         reject(err);
    //     //     }
    //     // });
    //     // return promise;
    // }

    // public async updateTicketStatus(tripUid: string, ticket: TTicket, status: TTicketStatus) {
    //     const promise = new Promise(async (resolve, reject) => {
    //         try {
    //             const collection = await this.firestorePathsService.getTripTicketsPath(tripUid);
    //             // const tick: TTicket = await this.firestoreService.getByUid(collection, ticket.uid);
    //             // if (tick) {

    //             ticket.status = status;
    //             switch (ticket.status) {
    //                 case 'assigned':
    //                     ticket.assignedDate = Date.now();
    //                     break;

    //                 case 'enroute':
    //                     ticket.enrouteDate = Date.now();
    //                     ticket.driver = this.sessionService.getUsersFullName();
    //                     break;

    //                 case 'current':
    //                     ticket.currentDate = Date.now();
    //                     break;

    //                 case 'completed':
    //                     ticket.completedDate = Date.now();
    //                     ticket.completedDate = mom-ent().valueOf();
    //                     break;
    //             }

    //             await this.firestoreService.update(collection, ticket);
    //             // }
    //             resolve(true);
    //         } catch (err) {
    //             reject(err);
    //         }
    //     });
    //     return promise;
    // }

    public async endTrip(tripUid: string, tickets: TTicket[], totalPositions: number): Promise<void> {
        const trip: TTrip = await this.getByUid(tripUid);
        trip.tickets = tickets;
        trip.totalPositions = totalPositions;
        trip.type = (trip.totalPositions === 0) ? 'Simulated' : 'Actual';
        trip.status = 'completed';
        trip.endTripDate = this.dateService.now();
        await this.update(trip);
    }

    // https://stackoverflow.com/questions/41381948/firebase-query-for-data-within-a-date-range
    public async getByDateRange(startRangeTimestamp: TTimestamp, endRangeTimestamp: TTimestamp): Promise<TTrip[]> {
        const path = this.firestorePathsService.getTripsPath();

        const queryOptions: TQueryOptions = {
            collectionPath: path,
            orderByField: 'startTripDate',
            orderByDirection: 'asc',  // Use 'asc' for chronological order
            startAt: startRangeTimestamp,
            endAt: endRangeTimestamp
        };

        const rawTrips = await this.firestoreService.queryCollection(queryOptions) as TTrip[];        
        const trips: TTrip[] = [];
        rawTrips.forEach((trip) => {
            // const trip = doc.data();
            // trip.uid = doc.id;
            
            // There were many times I started the Navigation and never completed the trip.
            // This inaction prevents many fields from being completed.  For now skip these
            // trips.
            if (!trip.endTripDate) {
                // console.warn(`Trip.uid = ${trip.uid}; trip.endTripDate field not found!  Skipping trip.`);
            } else if (!trip.tickets) {
                // console .warn(`Trip.uid = ${trip.uid}; trip.tickets not found!  Skipping trip.`);
            } else {
                // console .log(`Trip.uid = ${trip.uid}; found trip.endTripDate and trip.tickets!`);
                trips.push(trip);
            }
            // console .log(item);
        });
        return trips;
    }

    // // moved to ticketsService
    // const allTickets: TTicket[] = [];
    // for (const ticketUid of allTicketUids) {
    //     const ticket = await this.ticketsService.getByUid(ticketUid);
    //     allTickets.push(ticket);
    // }
}
